import { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { paradigmLightBlue, sanJuan } from '@paradigm/design-system/src/colors';
import { useCounterpartiesService } from '@paradigm/features/src/unified-rfqs/contexts/counterparties';
import { createCpsFilter } from '@paradigm/features/src/unified-rfqs/services/counterparties/cps-filter';

import { useObservable } from '@paradigm/utils/src/hooks';
import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import Button from '@paradigm/design-system/src/components/Button';
import { useTrackAction } from '@paradigm/features/src/analytics';

interface Props {
  readonly showLps?: boolean;
  readonly showFavorites?: boolean;
  readonly showClear?: boolean;
}

export default function Filters({
  showClear = false,
  showFavorites = false,
  showLps = false,
}: Props) {
  const cpsService = useCounterpartiesService();
  const cpsFilter = useMemo(() => createCpsFilter(cpsService), [cpsService]);
  const filterStatus = useObservable(cpsFilter.status);
  const trackAction = useTrackAction();

  const handleClear = useCallback(() => {
    trackAction('clear counterparties');
    cpsFilter.clear();
  }, [cpsFilter, trackAction]);

  const handleFavorites = useCallback(() => {
    trackAction('select favorites');
    cpsFilter.selectAllFavoritesOnly();
  }, [cpsFilter, trackAction]);

  const handleLps = useCallback(() => {
    trackAction('select lps');
    cpsFilter.selectAllLpsOnly();
  }, [cpsFilter, trackAction]);

  const clearButton = showClear && (
    <ClearButton onClick={handleClear} data-testid="clear-counterparties" />
  );

  const lps = showLps && (
    <PillButton
      ariaLabel="select lps"
      aria-pressed={filterStatus.allLpsSelectedOnly}
      className={pillClassName(filterStatus.allLpsSelectedOnly)}
      onClick={handleLps}
    >
      LPs
    </PillButton>
  );

  const favorites = showFavorites && (
    <PillButton
      ariaLabel="select favorites"
      className={pillClassName(filterStatus.allFavoritesSelectedOnly)}
      onClick={handleFavorites}
    >
      Favorites
    </PillButton>
  );

  useEffect(() => cpsFilter.teardown, [cpsFilter]);

  return (
    <FiltersContainer>
      {lps}
      {favorites}
      {clearButton}
    </FiltersContainer>
  );
}

function pillClassName(filled: boolean) {
  return filled ? 'filled' : undefined;
}

export const FiltersContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto 1fr;
  gap: 10px;
  margin: 0 0 0.75rem;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-bottom: 1rem;
  }
`;

const PillButton = styled(Button).attrs({ size: 'extra-small', outline: true })`
  border: 1px solid ${paradigmLightBlue}4d;

  transition: box-shadow 0.1s ease, color 0.1s ease;

  :focus-visible {
    box-shadow: 0 0 0 1px ${paradigmLightBlue};
  }

  :hover {
    color: ${paradigmLightBlue};
  }

  &.filled {
    background-color: ${paradigmLightBlue};
    color: white;

    :focus-visible {
      box-shadow: 0 0 0 1px white;
    }

    :hover {
      color: ${sanJuan};
    }
  }

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    height: auto;
    line-height: calc(2rem - 2px);
    border-radius: 1rem;
    font-size: 1rem;
  }
`;

const ClearButton = styled(Button).attrs({
  children: 'Clear Selection',
  ariaLabel: 'clear selection',
  variant: 'link',
  size: 'extra-small',
})`
  justify-self: end;
  color: ${paradigmLightBlue};
  font-size: 13px;
  line-height: 17px;
  padding: 2px;
  margin-right: -2px;
  border-radius: 3px;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 1rem;
  }
`;
